import React, {Component, ReactElement} from 'react'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {Messages} from '../../../../Messages'
import styled, {css} from 'styled-components'
import {EditLink} from '../../../../styledComponents/adminPage/Links'
import {Dialog, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {NatAssocWorkReportRepo} from '../repository/NatAssocWorkReportRepo'
import {AssocOfCondominium, NatGetAssocList} from '../repository/NatGetAssocList'
import {MESSAGE_TYPE, PUBLISHING_SETTING, ROLE_FOR_ADMINISTRATOR} from '../../../../CodeMaster'
import {PageMainArea} from '../../../../components/shared/hoc/withAdminPageTemplate'
import {LightGrayTableHead} from '../../../../styledComponents/adminPage/Table'
import NatAdminReportPublishingSettingEdit from './admin/NatAdminReportPublishingSettingEdit'
import {Colors} from '../../../../styledComponents/Constants'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper,
} from '../../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    assocWorkReportRepo: NatAssocWorkReportRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    assocList: AssocOfCondominium[]
    selectedAssocInfo: Nullable<AssocOfCondominium>
    isOpen: boolean
    isLoadCompleted: boolean
}

class NatAdminReportPublishingScopePage extends Component<Props & AdminPagePropsSet, States> {
    loginUserRoleId: Nullable<string>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            assocList: [],
            selectedAssocInfo: null,
            isOpen: false,
            isLoadCompleted: false,
        }
        this.loginUserRoleId = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.LOGIN_USER_ROLE_ID)

        this.handleClickSubmitOnSettingEditDialog = this.handleClickSubmitOnSettingEditDialog.bind(this)
        this.handleClickCancelOnSettingEditDialog = this.handleClickCancelOnSettingEditDialog.bind(this)
        this.handleSettingEditDialogClick = this.handleSettingEditDialogClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const assocList = await this.getAssocList()

            if (!assocList) {
                return
            }
            this.setState({
                assocList: assocList,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getAssocList(): Promise<Nullable<AssocOfCondominium[]>> {
        const {condominiumCd, assocWorkReportRepo, eventEmitter} = this.props

        let response: Nullable<NatGetAssocList>
        if (this.loginUserRoleId === ROLE_FOR_ADMINISTRATOR.ASSOC.value) {
            response = await this.props.asyncCallFunction(
                assocWorkReportRepo.getAssocListForChairman.bind(assocWorkReportRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
        } else {
            response = await this.props.asyncCallFunction(
                assocWorkReportRepo.getAssocList.bind(assocWorkReportRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
        }

        return response ? response.details : response
    }

    async handleClickSubmitOnSettingEditDialog(selectedAssocInfo: AssocOfCondominium): Promise<void> {
        await this.props.eventEmitter.showHeadingAreaMessage({
            title: null,
            messageLevel: MESSAGE_TYPE.INFORMATION,
            detail: Messages.IOPCC0005.toString('管理組合', selectedAssocInfo.assocName),
        })
        this.setState({isOpen: false})
        const assocList = await this.getAssocList()

        if (!assocList) {
            return
        }
        this.setState({
            assocList: assocList,
        })
    }

    handleClickCancelOnSettingEditDialog(): void {
        this.setState({isOpen: false})
    }

    handleSettingEditDialogClick(selectedAssocInfo: AssocOfCondominium): void {
        this.setState({
            isOpen: true,
            selectedAssocInfo: selectedAssocInfo,
        })
    }

    renderReportPublishingSettingRow(assoc: AssocOfCondominium): Nullable<ReactElement> {

        return (
            <TableRow key={`${assoc.assocId}`}>
                <AssocNameCell>
                    {assoc.assocName}
                </AssocNameCell>
                <PublishingScopeCell>
                    <FullWidthTable>
                        <TableBody>
                            <TableRow>
                                <ChairmanTableCell>
                                    ✓
                                </ChairmanTableCell>
                                <OtherDirectorTableCell>
                                    {
                                        assoc.reportPublishingSetting
                                        === PUBLISHING_SETTING.PUBLIC.value ?
                                            '✓' :
                                            ''
                                    }
                                </OtherDirectorTableCell>
                            </TableRow>
                        </TableBody>
                    </FullWidthTable>
                </PublishingScopeCell>
                <OperationCell>
                    <EditLink onClick={() => this.handleSettingEditDialogClick(assoc)}>
                        編集
                    </EditLink>
                </OperationCell>
            </TableRow>
        )
    }

    renderReportPublishingSettingTable(): Nullable<ReactElement> {
        const {assocList} = this.state

        return (
            <FixedPageMainArea>
                <TableFixed>
                    <LightGrayTableHead>
                        <TableRow>
                            <AssocNameCell>組合名</AssocNameCell>
                            <PublishingScopeCell>
                                <FullWidthTable>
                                    <TableBody>
                                        <TableRow>
                                            <ReportPublishingScopeCell colSpan={2}>
                                                報告書の公開範囲
                                            </ReportPublishingScopeCell>
                                        </TableRow>
                                        <TableRow>
                                            <ChairmanTableCell>理事長</ChairmanTableCell>
                                            <OtherDirectorTableCell>その他役員</OtherDirectorTableCell>
                                        </TableRow>
                                    </TableBody>
                                </FullWidthTable>
                            </PublishingScopeCell>
                            <OperationCell/>
                        </TableRow>
                    </LightGrayTableHead>
                    <TableBody>
                        {assocList.map(assoc =>
                            this.renderReportPublishingSettingRow(assoc),
                        )}
                    </TableBody>
                </TableFixed>
            </FixedPageMainArea>
        )
    }

    render(): Nullable<ReactElement> {
        const {
            isLoadCompleted,
            isOpen,
            selectedAssocInfo,
        } = this.state

        if (!isLoadCompleted) {
            return null
        }

        return (
            <>
                {this.renderReportPublishingSettingTable()}
                {selectedAssocInfo &&
                    <Dialog
                        id={'pp-dialog'}
                        open={isOpen}
                        transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                        maxWidth={'lg'}
                    >
                        <NatAdminReportPublishingSettingEdit
                            onSubmit={this.handleClickSubmitOnSettingEditDialog}
                            onCancel={this.handleClickCancelOnSettingEditDialog}
                            assocWorkReportRepo={this.props.assocWorkReportRepo}
                            condominiumCd={this.props.condominiumCd}
                            assocInfo={selectedAssocInfo}
                            eventEmitter={this.props.eventEmitter}
                        />
                    </Dialog>
                }
            </>
        )
    }
}

const TableFixed = styled(Table)`
  table-layout: fixed;
  width: auto;
`

const FullWidthTable = styled(Table)`
  width: 100%;
`

const ChairmanTableCell = styled(TableCell)`
  display: table-cell;
  table-layout: auto;
  width: 50%;
  vertical-align: inherit;
  padding: 0 20px 0 0;
  text-align: center;
  color: ${Colors.Body};

  &.MuiTableCell-root {
    border-bottom: none;
  }
`

const OtherDirectorTableCell = styled(TableCell)`
  display: table-cell;
  table-layout: auto;
  width: 50%;
  vertical-align: inherit;
  padding: 0 0 0 20px;
  text-align: center;
  color: ${Colors.Body};

  &.MuiTableCell-root {
    border-bottom: none;
  }
`

const TableCellBaseCss = css`
  padding-top: 14px;
  padding-bottom: 14px;
  font-size: 14px;
  line-height: 21px;
`

const AssocNameCell = styled(TableCell)`
  color: ${Colors.Body};

  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-left: 24px;
    min-width: 418px;
  }
`

const ReportPublishingScopeCell = styled(TableCell)`
  color: ${Colors.Body};

  &.MuiTableCell-root {
    ${TableCellBaseCss};
    white-space: nowrap;
    padding: 0 0 4px 0;
    border-bottom: none;
  }
`

const PublishingScopeCell = styled(TableCell)`
  color: ${Colors.Body};

  &.MuiTableCell-root {
    ${TableCellBaseCss};
    white-space: nowrap;
    padding-left: 0;
  }
`

const OperationCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    text-align: center;
    white-space: nowrap;
    width: 136px
  }
`

const FixedPageMainArea = styled(PageMainArea)`
  min-width: 706px;
  width: fit-content;
`

export default createAdminPage(NatAdminReportPublishingScopePage)
